// Where the recordings actually live.
//
// The bug this module exists to close: URL.createObjectURL() hands back a
// string that only means anything to the page that made it. Written into a
// margin note and read back after a reload, it is a dead link — the row is
// there, the duration is there, and the voice is gone. For an app whose whole
// job is "keep what she said", that is the app not working.
//
// So a margin note's `mediaUrl` stops being an object URL and becomes a
// durable reference: `idb:<key>`. The bytes live here, in this module's own
// IndexedDB database, and an object URL is minted from them on demand at play
// time.
//
// Why a database of its own, and not the chassis `records` store: that store
// is the generated entity store, keyed on the single id sequence store.js
// allocates row ids from. A blob written there would eventually take an id a
// row wanted, and every `records.all()` — which is how the entire app hydrates
// on boot — would drag every recording ever made through memory before the
// first screen paints.
//
// Blobs are stored as Blobs. IndexedDB stores them natively; base64 would
// inflate them by a third and cost a decode on every play.

const DB_NAME = 'handed-down-audio';
const DB_VERSION = 1;
const STORE = 'clips';

/** The scheme that marks a mediaUrl as one of ours. */
export const AUDIO_REF_PREFIX = 'idb:';

// --- references -------------------------------------------------------------
// Pure string work, no IndexedDB. Everything below can be reasoned about — and
// tested — without a browser.

/** `<key>` -> `idb:<key>`. */
export function makeAudioRef(key) {
  return AUDIO_REF_PREFIX + String(key);
}

/**
 * The key inside a durable reference, or null if this is not one of ours.
 * Tolerant on purpose: it accepts a bare key as well as a full `idb:` ref, so
 * a caller that kept hold of the raw key still resolves.
 */
export function audioKey(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith(AUDIO_REF_PREFIX)) {
    const key = trimmed.slice(AUDIO_REF_PREFIX.length).trim();
    return key || null;
  }
  // A bare key we minted: hex/uuid shaped, no scheme, no slashes.
  if (/^[0-9a-f][0-9a-f-]{7,63}$/i.test(trimmed) && !trimmed.includes(':')) return trimmed;
  return null;
}

/** True for a mediaUrl this module can resolve to bytes. */
export function isAudioRef(value) {
  return typeof value === 'string' && value.trim().startsWith(AUDIO_REF_PREFIX) && !!audioKey(value);
}

/**
 * A URL a browser can load directly — an http(s) address, a data: URI, a
 * native file URL, or an app-relative path. Explicitly NOT `blob:`: those are
 * the object URLs this whole change exists to stop trusting, and one read back
 * from storage is always dead.
 */
export function isDirectUrl(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (!v) return false;
  return /^(https?:\/\/|data:|file:\/\/|capacitor:\/\/|ionic:\/\/|\/)/i.test(v);
}

/**
 * True when a mediaUrl will still mean something after the app is closed and
 * reopened. Notes written before this change carry either `''` or a `blob:`
 * object URL; both are false here, which is what makes them render as a note
 * with no recording instead of a broken one.
 */
export function isDurableMedia(value) {
  return isAudioRef(value) || isDirectUrl(value);
}

function mintKey() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* fall through */ }
  const rand = Math.random().toString(16).slice(2, 10) + Math.random().toString(16).slice(2, 10);
  return `${Date.now().toString(16)}-${rand}`;
}

// --- errors -----------------------------------------------------------------
// A failed write is the one thing here that must be loud. Losing a recording
// quietly is the worst thing this app could do, so putAudio throws and the
// caller is expected to say so out loud.

/** Error thrown by putAudio. `.code` is 'empty' | 'unavailable' | 'write-failed'. */
export class AudioSaveError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = 'AudioSaveError';
    this.code = code;
    this.cause = cause;
  }
}

const fail = (code, message, cause) => new AudioSaveError(code, message, cause);

// --- the database ------------------------------------------------------------

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined' || !indexedDB) {
      reject(fail('unavailable', 'This browser has nowhere to keep recordings.'));
      return;
    }
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (err) {
      reject(fail('unavailable', 'This browser has nowhere to keep recordings.', err));
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(fail('unavailable', 'This browser has nowhere to keep recordings.', req.error));
    req.onblocked = () => reject(fail('unavailable', 'Another copy of this app is open and holding the recordings.'));
  }).catch((err) => {
    // Never cache a rejection: private mode can be left, a blocked upgrade can
    // clear, and the next recording deserves a fresh try.
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

function runTx(mode, fn) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        let t;
        try {
          t = db.transaction(STORE, mode);
        } catch (err) {
          reject(err);
          return;
        }
        let out;
        try {
          out = fn(t.objectStore(STORE));
        } catch (err) {
          reject(err);
          return;
        }
        // Resolve on complete, not on the request's own success: only a
        // completed transaction means the bytes are actually committed.
        t.oncomplete = () => resolve(out && typeof out === 'object' && 'result' in out ? out.result : out);
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error ?? new Error('The write was aborted.'));
      }),
  );
}

// --- writing -----------------------------------------------------------------

/**
 * Write a recording and return the durable reference to store on the note.
 *
 * Throws AudioSaveError on failure — quota exceeded, private mode, no
 * IndexedDB at all. The caller must tell the person their recording was not
 * saved rather than filing a note that points at nothing.
 *
 * @param {Blob} blob
 * @returns {Promise<string>} `idb:<key>`
 */
export async function putAudio(blob) {
  if (!blob || typeof blob.size !== 'number') {
    throw fail('empty', 'There was no recording to save.');
  }
  if (blob.size === 0) {
    throw fail('empty', 'Nothing came through the microphone, so there was nothing to save.');
  }

  const key = mintKey();
  const base = {
    key,
    type: blob.type || 'audio/webm',
    size: blob.size,
    savedAt: Date.now(),
  };

  try {
    await runTx('readwrite', (s) => s.put({ ...base, blob }));
    return makeAudioRef(key);
  } catch (err) {
    // If the database could not be opened at all, the Blob is not the problem
    // and retrying with an ArrayBuffer only replaces a true reason with a
    // vaguer one. Report what actually happened.
    if (err instanceof AudioSaveError && err.code === 'unavailable') throw err;

    // A handful of older WebKit builds accept a structured clone of everything
    // except a Blob. Falling back to the raw bytes keeps this honest — still
    // binary, still no base64 inflation — and costs one extra copy only on the
    // browsers that need it.
    let buffer = null;
    try {
      buffer = await blob.arrayBuffer();
    } catch { /* nothing more to try */ }

    if (!buffer) {
      throw fail('write-failed', 'That recording could not be saved.', err);
    }
    try {
      await runTx('readwrite', (s) => s.put({ ...base, buffer }));
      return makeAudioRef(key);
    } catch (retryErr) {
      throw fail('write-failed', 'That recording could not be saved.', retryErr);
    }
  }
}

// --- reading -----------------------------------------------------------------

/**
 * The stored blob for a reference, or null.
 *
 * A missing key is a normal outcome, not an exception: a note whose recording
 * was never saved, or was evicted by the browser, is a note with no recording.
 */
export async function getAudioBlob(ref) {
  const key = audioKey(ref);
  if (!key) return null;

  let rec;
  try {
    rec = await runTx('readonly', (s) => s.get(key));
  } catch {
    return null;
  }
  if (!rec) return null;

  if (rec.blob) return rec.blob;
  if (rec.buffer) {
    try {
      return new Blob([rec.buffer], { type: rec.type || 'audio/webm' });
    } catch {
      return null;
    }
  }
  return null;
}

// One object URL per key, held for the life of the page. Minting a fresh one
// on every press would leak one per play — and the browser holds the whole
// blob alive behind each.
const urlCache = new Map();
const inFlight = new Map();

/**
 * A playable object URL for a stored recording, or null if there is nothing
 * stored under that reference.
 *
 * @param {string} ref  `idb:<key>` as held in a note's mediaUrl
 * @returns {Promise<string|null>}
 */
export function getAudioUrl(ref) {
  const key = audioKey(ref);
  if (!key) return Promise.resolve(null);
  if (urlCache.has(key)) return Promise.resolve(urlCache.get(key));
  if (inFlight.has(key)) return inFlight.get(key);

  const work = getAudioBlob(ref)
    .then((blob) => {
      if (!blob) return null;
      // Two presses in the same tick both got here; the first one to land wins
      // and the second reuses it rather than minting a second URL.
      if (urlCache.has(key)) return urlCache.get(key);
      let url;
      try {
        url = URL.createObjectURL(blob);
      } catch {
        return null;
      }
      urlCache.set(key, url);
      return url;
    })
    .catch(() => null)
    .finally(() => inFlight.delete(key));

  inFlight.set(key, work);
  return work;
}

/** True if there are bytes behind this reference. */
export async function hasAudio(ref) {
  return (await getAudioBlob(ref)) !== null;
}

// --- forgetting ---------------------------------------------------------------

/** Revoke and forget the cached object URL for one reference. */
export function releaseAudioUrl(ref) {
  const key = audioKey(ref);
  if (!key) return;
  const url = urlCache.get(key);
  if (!url) return;
  urlCache.delete(key);
  try {
    URL.revokeObjectURL(url);
  } catch { /* already gone */ }
}

/** Revoke and forget every cached object URL. */
export function releaseAudioUrls() {
  for (const url of urlCache.values()) {
    try {
      URL.revokeObjectURL(url);
    } catch { /* already gone */ }
  }
  urlCache.clear();
}

/**
 * Delete a recording for good. Resolves true if it was deleted, false if there
 * was nothing to delete or the store could not be reached — deleting something
 * that is already absent is not an error.
 */
export async function deleteAudio(ref) {
  const key = audioKey(ref);
  if (!key) return false;
  releaseAudioUrl(ref);
  try {
    await runTx('readwrite', (s) => s.delete(key));
    return true;
  } catch {
    return false;
  }
}

/** Testing seam: drop the cached connection so a fresh database can be opened. */
export function _resetForTests() {
  dbPromise = null;
  urlCache.clear();
  inFlight.clear();
}
